import { Flag, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { TeamStablefordPayload } from '@golf-app/shared';

import { PlayerAvatar } from '@/components/PlayerAvatar';
import { TeamAvatar } from '@/components/TeamAvatar';
import { buildTeamStablefordSummaries } from '@/lib/teamStablefordOverview';

export interface StablefordOverviewRound {
  id: string;
  name: string;
  roundNumber: number;
  status: string;
  courseNameSnapshot: string | null;
  defaultTeeNameSnapshot: string | null;
}

interface Props {
  eventId: string;
  rounds: StablefordOverviewRound[];
  stablefordByRound: Record<string, TeamStablefordPayload>;
  isManager: boolean;
}

function signed(value: number, digits = 1): string {
  const rounded = Number(value.toFixed(digits));
  return `${rounded > 0 ? '+' : ''}${rounded}`;
}

function roundMeta(round: StablefordOverviewRound): string {
  return [round.courseNameSnapshot, round.defaultTeeNameSnapshot].filter(Boolean).join(' · ');
}

export function StablefordTeamsOverview({ eventId, rounds, stablefordByRound, isManager }: Props) {
  const firstPayload = rounds.map((round) => stablefordByRound[round.id]).find(Boolean);
  const teamCount = firstPayload?.teams.length ?? 0;

  return (
    <div className="team-stableford-overview">
      <div className="team-stableford-actions">
        {rounds.length === 1 ? (
          <Link className="team-stableford-action" to={`/app/tournament-rounds/${rounds[0]!.id}/stableford`}>
            <Flag aria-hidden="true" /> Open scoring
          </Link>
        ) : null}
        {isManager ? (
          <Link className="team-stableford-action" to={`/app/tournaments/${eventId}/teams/manage`}>
            <Users aria-hidden="true" /> Manage rosters
          </Link>
        ) : null}
      </div>

      <header className="team-stableford-hero">
        <span className="t-label">{teamCount} teams · aggregate scoring</span>
        <h1>Team Stableford</h1>
        <p>
          Each team total is the sum of every player’s net result after the per-player cap.
          Gross points and uncapped individual net scores stay visible below.
        </p>
      </header>

      {rounds.map((round) => {
        const payload = stablefordByRound[round.id];
        if (!payload) {
          return (
            <section className="team-stableford-round" key={round.id}>
              <div className="t-empty">
                <div className="t-empty-title">Stableford scores unavailable</div>
                <div className="t-empty-hint">Refresh the page or open the round setup to verify its scoring settings.</div>
              </div>
            </section>
          );
        }

        const summaries = buildTeamStablefordSummaries(payload);
        const cap = payload.config.teamRules?.playerContributionLimit ?? 4;

        return (
          <section className="team-stableford-round" key={round.id} aria-labelledby={`stableford-round-${round.id}`}>
            <div className="team-stableford-round-heading">
              <div>
                <span className="t-label">Round {round.roundNumber} · {round.status}</span>
                <h2 id={`stableford-round-${round.id}`}>{round.name}</h2>
                {roundMeta(round) ? <p>{roundMeta(round)}</p> : null}
              </div>
              <div className="team-stableford-round-meta">
                <span>{payload.config.points.albatrossOrBetter}/{payload.config.points.eagle}/{payload.config.points.birdie}/{payload.config.points.par}/{payload.config.points.bogey}/{payload.config.points.doubleBogeyOrWorse}</span>
                <small>Points table · player cap ±{cap}</small>
              </div>
              <Link className="team-stableford-action" to={`/app/tournament-rounds/${round.id}/stableford`}>
                View round
              </Link>
            </div>

            {!payload.setupReady ? (
              <div className="t-empty team-stableford-empty">
                <div className="t-empty-title">Team roster not set up</div>
                <div className="t-empty-hint">Open the round and snapshot the assigned players before scoring.</div>
              </div>
            ) : (
              <div className="team-stableford-grid">
                {summaries.map((summary) => (
                  <article className="team-stableford-card" key={summary.team.id}>
                    <header className="team-stableford-card-heading">
                      <span className="team-stableford-rank">{summary.rank}</span>
                      <TeamAvatar team={summary.team} size={36} />
                      <div className="team-stableford-team-name">
                        <h3>{summary.team.name}</h3>
                        <p>{summary.players.length} players · {summary.grossPoints} gross pts</p>
                      </div>
                      <div className="team-stableford-team-total">
                        <strong>{signed(summary.teamTotal)}</strong>
                        <span>Team net total</span>
                      </div>
                    </header>

                    <div className="team-stableford-player-labels" aria-hidden="true">
                      <span>Player score</span>
                      <span>Gross</span>
                      <span>Net</span>
                      <span>Team</span>
                    </div>
                    <div className="team-stableford-player-list">
                      {summary.players.map((player) => (
                        <div className="team-stableford-player-row" key={player.eventPlayerId}>
                          <div className="team-stableford-player-name">
                            <PlayerAvatar avatarUrl={player.avatarUrl} displayName={player.displayName} size={30} />
                            <span>
                              {player.displayName}
                              <small>Quota {player.quota} · {player.holesPlayed}/{payload.holes.length} holes</small>
                            </span>
                          </div>
                          <span className="team-stableford-player-value" aria-label={`${player.displayName} gross points`}>
                            <small>Gross</small>{player.grossPoints}
                          </span>
                          <span className="team-stableford-player-value" aria-label={`${player.displayName} net score`}>
                            <small>Net</small>{signed(player.netPoints)}
                          </span>
                          <span className="team-stableford-player-value team-stableford-player-team" aria-label={`${player.displayName} team contribution`}>
                            <small>Team</small>{signed(player.teamContribution)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
